import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {catchError, from, map, Observable, switchMap, throwError} from 'rxjs';
import {ServerEndpoints} from '../../../../server.endpoints';
import {FiscalJobDetail} from '../../_models/fiscal-jobs/fiscal-job.models';
import {TseExportCreateRequest, TseExportFile} from '../../_models/exports/tse-export.models';
import {DataExportHttpService} from './data-export-http.service';

/**
 * The typed HTTP surface of the TSE data export (#776) — two calls, no state.
 *
 * ### Why listing past exports is NOT here
 *
 * A past export is a fiscal job, and the engine's own `/fiscal/jobs/List?kind=tseExport` is the queryable
 * record of one (#768). Adding a second list here would be a second, weaker copy of state the engine already
 * holds — which is exactly what makes a list and a detail view come to disagree.
 *
 * ### And there is no "which devices" call either
 *
 * Unlike the DSFinV-K export, which lists registers for its filter, this export is never narrowed: which
 * security devices a period covers is derived server-side from the location's own record of which ones it
 * used. Offering it as a picker would invite narrowing an artefact whose value is that it is not narrowed.
 *
 * ### Why a download is a request and not an `<a href>`
 *
 * The session's bearer token travels in an `Authorization` header that an interceptor adds; a plain link
 * carries none, so the browser would fetch an anonymous request and get a 401. The file is therefore read as
 * a blob and saved from memory.
 *
 * ### The file name is the SERVER'S
 *
 * `FiscalExportNaming` decides it, because a file name is the one piece of this feature that leaves the
 * application — it is saved to a disk, attached to an e-mail and handed to a tax office, and it keeps
 * whatever it says. It is read out of `Content-Disposition`, which the gateway exposes for exactly this. The
 * fallback is deliberately GENERIC rather than a second implementation of the naming rule: a
 * re-implementation would drift from the server's silently.
 *
 * ### A failed download's body is decoded before it is re-thrown
 *
 * `responseType: 'blob'` applies to the error body as well, so a refusal would otherwise arrive as a `Blob`
 * and `error.error.code` would be `undefined` — every catalogued refusal of a download collapsing into the
 * generic fallback sentence. The decoding is shared with the DSFinV-K download rather than written twice:
 * it is a property of `responseType: 'blob'`, not of either export.
 */
@Injectable({providedIn: 'root'})
export class TseExportHttpService {
  /**
   * What the download is called when the server's own name could not be read.
   *
   * **`.tar`, because that is what the file IS.** The API that produces it declares no other container. The
   * fallback still carries no period, because a name that looks like the contract but was guessed is worse
   * than one nobody can mistake for it.
   */
  static readonly FALLBACK_FILE_NAME = 'TSE-Export.tar';

  // `BASE_URL` is a string DI token provided by the app bootstrap, so it is taken through @Inject rather than
  // inject() — the same shape every other http service in this module uses.
  constructor(@Inject('BASE_URL') private readonly baseUrl: string, private readonly http: HttpClient) {
  }

  /**
   * Starts a TSE data-export run.
   *
   * Answers as soon as the run is durable and scheduled — it does not wait for the file. The caller then
   * watches it through the fiscal job store.
   */
  create(request: TseExportCreateRequest): Observable<FiscalJobDetail> {
    return this.http.post<FiscalJobDetail>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_TseExports_Create}`, request);
  }

  /**
   * Collects a finished export's file.
   *
   * **Never rebuilds.** The server reads the reference the run's entry already holds and fetches that one
   * artefact, so asking again returns the same archive — until the far end expires it, which it answers with
   * its own code and its own instruction.
   */
  download(jobId: string, itemId: string): Observable<TseExportFile> {
    const params = new HttpParams().set('jobId', jobId).set('itemId', itemId);

    return this.http
      .get(`${this.baseUrl}${ServerEndpoints.Fiscal_TseExports_Download}`,
        {params, observe: 'response', responseType: 'blob'})
      .pipe(
        map(response => ({
          fileName: DataExportHttpService.readFileName(response, TseExportHttpService.FALLBACK_FILE_NAME),
          blob: response.body ?? new Blob()
        })),
        catchError((error: HttpErrorResponse) => TseExportHttpService.withReadableError(error)));
  }

  /**
   * Re-throws a failed download with its `ApiError` body readable.
   *
   * **Why this is needed at all.** `responseType: 'blob'` applies to the ERROR body too, so a refusal arrives
   * as `error.error instanceof Blob` and `err.error.code` — the value every caller branches on and the whole
   * point of the catalogued codes — is `undefined`. Every refusal of a download would therefore reach the
   * operator as the generic fallback sentence, whatever the server had actually said.
   *
   * A body that is not JSON is passed through untouched rather than swallowed — an empty or HTML error page
   * is still an error.
   */
  private static withReadableError(error: HttpErrorResponse): Observable<never> {
    if (!(error.error instanceof Blob)) return throwError(() => error);

    return from(error.error.text()).pipe(
      switchMap(text => throwError(() => new HttpErrorResponse({
        error: TseExportHttpService.parseJson(text),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined
      })))
    );
  }

  /** The body as an object when it is JSON, and the raw text when it is not. */
  private static parseJson(text: string): unknown {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
}
